/**
 * The HubSpot and Qonto logos, before their name wherever it is a label
 * (Pascal, 29/09/2026 : « peux-tu mettre les logos de HubSpot et de Qonto
 * partout où leur nom est cité dans l'application ? »). Label = source pill,
 * column header, section title, metric source, connector name, button or
 * link, status chip. Never inside a running sentence, a tooltip, an
 * aria-label, a toast or an <option> (EXIGENCES.md §11).
 *
 * Inline SVG only: no network, no external URL. HubSpot is the Simple Icons
 * path (CC0) in its orange #FF7A59; Qonto is its black rounded square with
 * four white petals (a thin light outline keeps it visible in dark mode).
 * The logo sits BEFORE the whole label and the label stays one text node, so
 * `getByText('Factures Qonto')` still finds it; it carries no <title>, so the
 * surrounding text content and accessible names are unchanged.
 */
import type { ReactNode } from 'react';

export type Provider = 'hubspot' | 'qonto';
export type ProviderLogoSize = 'sm' | 'md';

export const PROVIDER_NAME: Record<Provider, string> = { hubspot: 'HubSpot', qonto: 'Qonto' };

const SIZE_PX: Record<ProviderLogoSize, number> = { sm: 14, md: 16 };

const HUBSPOT_PATH =
  'M18.164 7.93V5.084a2.198 2.198 0 001.267-1.978v-.067A2.2 2.2 0 0017.238.845h-.067a2.2 2.2 0 00-2.193 2.193v.067a2.196 2.196 0 001.252 1.973l.013.006v2.852a6.22 6.22 0 00-2.969 1.31l.012-.01-7.828-6.095A2.497 2.497 0 104.3 4.656l-.012.006 7.697 5.991a6.176 6.176 0 00-1.038 3.446c0 1.343.425 2.588 1.147 3.607l-.013-.02-2.342 2.343a1.968 1.968 0 00-.58-.095h-.002a2.033 2.033 0 102.033 2.033 1.978 1.978 0 00-.1-.595l.005.014 2.317-2.317a6.247 6.247 0 104.782-11.134l-.036-.005zm-.964 9.378a3.206 3.206 0 113.215-3.207v.002a3.206 3.206 0 01-3.207 3.207z';

const QONTO_PETALS = [
  'M12 12Q12 5.6 18.4 5.6Q18.4 12 12 12Z',
  'M12 12Q18.4 12 18.4 18.4Q12 18.4 12 12Z',
  'M12 12Q12 18.4 5.6 18.4Q5.6 12 12 12Z',
  'M12 12Q5.6 12 5.6 5.6Q12 5.6 12 12Z',
];

const PROVIDER_PATTERN: Array<[Provider, RegExp]> = [
  ['hubspot', /\bhubspot\b/i],
  ['qonto', /\bqonto\b/i],
];

/**
 * The provider named in a dynamic label (« HubSpot : À jour », « Factures
 * Qonto », a source string from the API), or null when the label names
 * neither, or both (one logo must never stand for the wrong source).
 */
export function providerOf(text: unknown): Provider | null {
  const found = providersOf(text);
  return found.length === 1 ? found[0] : null;
}

/**
 * Every provider named in a label, in reading order: the grouped source pill
 * « HubSpot · Qonto · Fibery » gets both logos, before the whole label.
 */
export function providersOf(text: unknown): Provider[] {
  if (typeof text !== 'string' || !text) return [];
  return PROVIDER_PATTERN
    .map(([provider, pattern]) => ({ provider, index: text.search(pattern) }))
    .filter((item) => item.index >= 0)
    .sort((left, right) => left.index - right.index)
    .map((item) => item.provider);
}

export function ProviderLogo({
  provider,
  size = 'sm',
  label = false,
  className = '',
}: {
  provider: Provider;
  /** sm = 14 px (chips, rows, buttons), md = 16 px (titles, connector names). */
  size?: ProviderLogoSize;
  /**
   * Decorative next to its visible name (default): hidden from assistive
   * tech. `label` names it (role img) when it stands alone.
   */
  label?: boolean;
  className?: string;
}) {
  const px = SIZE_PX[size];
  return (
    <svg
      className={`c-provider-logo c-provider-logo--${provider} c-provider-logo--${size}${className ? ` ${className}` : ''}`}
      viewBox="0 0 24 24"
      width={px}
      height={px}
      focusable="false"
      aria-hidden={label ? undefined : true}
      role={label ? 'img' : undefined}
      aria-label={label ? PROVIDER_NAME[provider] : undefined}
      data-provider-logo={provider}
    >
      {provider === 'hubspot' ? (
        <path fill="#FF7A59" d={HUBSPOT_PATH} />
      ) : (
        <>
          <rect className="c-provider-logo__tile" width="24" height="24" rx="5.5" fill="#000" />
          <g fill="#fff">
            {QONTO_PETALS.map((d) => <path key={d} d={d} />)}
          </g>
        </>
      )}
    </svg>
  );
}

/**
 * The logo followed by the label, without any wrapper: the text stays a
 * direct child of its parent, one text node. `provider` defaults to the one
 * named in a string label (providerOf); `null` draws no logo.
 */
export function ProviderName({
  provider,
  size = 'sm',
  children,
}: {
  provider?: Provider | null;
  size?: ProviderLogoSize;
  children: ReactNode;
}) {
  const resolved = provider === undefined ? providerOf(children) : provider;
  if (!resolved) return <>{children}</>;
  return (
    <>
      <ProviderLogo provider={resolved} size={size} />
      {children}
    </>
  );
}

/**
 * A label node with its provider logo when it is a string naming HubSpot or
 * Qonto (shared components: chips, titles, buttons); any other node as is.
 */
export function withProviderLogo(node: ReactNode, size: ProviderLogoSize = 'sm'): ReactNode {
  const providers = providersOf(node);
  if (!providers.length) return node;
  return (
    <>
      {providers.map((provider) => <ProviderLogo key={provider} provider={provider} size={size} />)}
      {node}
    </>
  );
}
